import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import ReduxStoreRoutes from '../../../../../core/models/static/routes/redux-store-routes';
import BroadcastService from '../../../../../core/services/broadcast';
import CacheService from '../../../../../core/services/cache/cache_service';
import CrispService from '../../../../../core/services/crisp';
import useAccessLevels from '../../../../hooks/use-access-levels';
import useIndustry from '../../../../hooks/use-industry';
import {setReduxDialogState} from '../../../../redux/slices/dialogs/actions';
import {usePanel} from '../../../layout';
import {usePanelEntriesCatalog, type PanelEntry} from '../../../../panel-entries';
import type {
    DynamicSearchIndustry,
    OpenCreateOnlineProfileArgs,
} from '../../../../panel-entries/adapters/dynamic-providers';
import type {TutorialsProviderServices} from '../../../../panel-entries/adapters/tutorials-provider';

const initOnlineProfileDialogState = {
    open: false,
    initialValues: null as OpenCreateOnlineProfileArgs['initialValues'] | null,
    onSuccess: (() => undefined) as OpenCreateOnlineProfileArgs['onSuccess'],
    onClose: (() => undefined) as OpenCreateOnlineProfileArgs['onClose'],
};

export interface PanelCommandSearchServices extends TutorialsProviderServices {
    canAccess: (accessKey: string) => boolean;
    openSettings: () => void;
    openSupportChat: () => void;
    openLogoutDialog: () => void;
    openQuickOrder: () => void;
    panelEntries: PanelEntry[];
    industries: DynamicSearchIndustry[];
    /** Default industry for keyword-only Nexus navigation when suggestions are disabled. */
    defaultIndustryId?: string | number;
    /** Mirrors Nexus config `canSeeAutocompleteSuggestions` / `showSuggestionInOperator`. */
    canSeeAutocompleteSuggestions: boolean;
    /** i18n helper for dynamic provider copy under `panel-sections.command-search`. */
    tCommandSearch: (selector: (t: any) => string, options?: Record<string, unknown>) => string;
    openCreateOnlineProfile: (args: OpenCreateOnlineProfileArgs) => void;
}

export interface UsePanelCommandSearchServicesResult {
    services: PanelCommandSearchServices;
    onlineProfileDialog: typeof initOnlineProfileDialogState;
}

/**
 * Builds the services object injected into every command-search provider's context —
 * access checks, panel-action handlers, the live panel-entries catalog, and search inputs
 * (industries, i18n) used by the dynamic providers.
 */
export const usePanelCommandSearchServices = (): UsePanelCommandSearchServicesResult => {
    const dispatch = useDispatch();
    const {packages} = useAccessLevels();
    const {setDrawersOpenState} = usePanel();
    const {industries, cachedIndustry} = useIndustry({noCategories: true}) as {
        industries: DynamicSearchIndustry[];
        cachedIndustry?: {id?: string | number} | null;
    };
    const defaultIndustryId = cachedIndustry?.id;
    const panelEntries = usePanelEntriesCatalog();
    const {t: tLogout} = useTranslation('components', {keyPrefix: 'panel-sections.app-bar.actions.logout'});
    const {t: bizLearnT} = useTranslation('components', {keyPrefix: 'panel-sections.app-bar.actions.biz-learn-videos'});
    const {t: tCommandSearch} = useTranslation('components', {keyPrefix: 'panel-sections.command-search'});
    const [nexusConfigs, setNexusConfigs] = useState(() => CacheService.getNexusSearchConfigs());
    const [onlineProfileDialog, setOnlineProfileDialog] = useState(initOnlineProfileDialogState);

    useEffect(() => {
        const {close} = BroadcastService.addEventListener(
            BroadcastService.channelNames.nexusSearchConfigs,
            'message',
            setNexusConfigs,
        );
        return () => close();
    }, []);

    const canAccess = useCallback((accessKey: string): boolean => {
        const [pkg, flag] = accessKey.split('.');
        return Boolean((packages as unknown as Record<string, Record<string, unknown>>)?.[pkg]?.[flag]);
    }, [packages]);

    const openSettings = useCallback(() => {
        setDrawersOpenState((prevState) => ({...prevState, settingsDrawer: !prevState.settingsDrawer}));
    }, [setDrawersOpenState]);

    const openSupportChat = useCallback(() => {
        if (CrispService.isChatOpened) {
            CrispService.closeChatBox();
        } else {
            CrispService.openChatBox();
        }
    }, []);

    const openLogoutDialog = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
            open: true,
            title: tLogout((t) => t.title),
            description: tLogout((t) => t.confirmationDescription),
            maxWidth: 'sm',
            fullWidth: true,
            onClose: async (confirmed: boolean) => {
                if (confirmed) {
                    await CacheService.logout(true);
                }
                return true;
            },
        }));
    }, [dispatch, tLogout]);

    const openQuickOrder = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.quickOrders, {open: true}));
    }, [dispatch]);

    const openCreateOnlineProfile = useCallback((args: OpenCreateOnlineProfileArgs) => {
        setOnlineProfileDialog({
            open: true,
            initialValues: args.initialValues,
            onSuccess: args.onSuccess,
            onClose: () => {
                args.onClose();
                setOnlineProfileDialog(initOnlineProfileDialogState);
            },
        });
    }, []);

    const services = useMemo((): PanelCommandSearchServices => ({
        canAccess,
        openSettings,
        openSupportChat,
        openLogoutDialog,
        openQuickOrder,
        panelEntries,
        industries,
        defaultIndustryId,
        canSeeAutocompleteSuggestions: Boolean(nexusConfigs?.canSeeAutocompleteSuggestions),
        tCommandSearch: tCommandSearch as PanelCommandSearchServices['tCommandSearch'],
        bizLearnT,
        openCreateOnlineProfile,
    }), [
        canAccess,
        openSettings,
        openSupportChat,
        openLogoutDialog,
        openQuickOrder,
        panelEntries,
        industries,
        defaultIndustryId,
        nexusConfigs?.canSeeAutocompleteSuggestions,
        tCommandSearch,
        bizLearnT,
        openCreateOnlineProfile,
    ]);

    return {
        services,
        onlineProfileDialog,
    };
};

export default usePanelCommandSearchServices;
